import React, { useState } from "react";
import styled, { css } from "styled-components";
import PropTypes from "prop-types";

export function Slider({
  onChange,
  tracHeight,
  thumbSize,
  filledTrackColor,
  min,
  max,
  defaultValue,
  labels,
  showMarks,
  ...props
}) {
  const [value, setValue] = useState(defaultValue);

  const handleInput = (event) => {
    const value = Number(event.target.value);
    setValue(value);
  };

  const handleTouchEnd = (event) => {
    const value = Number(event.target.value);
    onChange(value);
  };

  return (
    <Wrapper
      tracHeight={tracHeight}
      thumbSize={thumbSize}
      filledTrackColor={filledTrackColor}
      {...props}
      style={{ "--min": min, "--max": max, "--val": value }}
    >
      <NumberWrapper>
        <RangeNumber>{min}</RangeNumber>
        <ThumbNumber>{value}</ThumbNumber>
        <RangeNumber>{max}</RangeNumber>
      </NumberWrapper>
      <TrackWrapper>
        <Input
          onInput={handleInput}
          onTouchEnd={handleTouchEnd}
          onClick={handleTouchEnd}
          min={min}
          max={max}
          defaultValue={defaultValue}
        />
        <TicksMarksWrapper show={showMarks}>
          {[...Array(max - min + 1)].map((_, index) => (
            <TickMark key={`mark_${index}`} />
          ))}
        </TicksMarksWrapper>
      </TrackWrapper>
      <Labels>
        {labels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </Labels>
    </Wrapper>
  );
}

Slider.propTypes = {
  onChange: PropTypes.func,
  tracHeight: PropTypes.string,
  thumbSize: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  defaultValue: PropTypes.number,
  labels: PropTypes.arrayOf(PropTypes.string),
  showMarks: PropTypes.bool,
};

Slider.defaultProps = {
  tracHeight: "5px",
  thumbSize: "14px",
  min: 1,
  max: 10,
  defaultValue: 1,
  labels: [],
};

const Labels = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: 400;
  font-size: 0.875rem;
`;

const RangeNumber = styled.div`
  width: var(--thumb-size);
  display: flex;
  justify-content: center;
  text-align: center;
`;

const ThumbNumber = styled(RangeNumber)`
  position: absolute;
  color: #84a4fc;
  width: var(--thumb-size);
  height: var(--thumb-size);
  left: calc(var(--ratio) * (100% - var(--thumb-size)));
`;

const variables = ({ tracHeight, thumbSize, filledTrackColor }) => css`
  --track-height: ${tracHeight};
  --thumb-size: ${thumbSize};
  --range: calc(var(--max) - var(--min));
  --ratio: calc((var(--val) - var(--min)) / var(--range));
  --filled-track-size: calc(var(--ratio) * 100%);
  --track-color: #e1e1e1;
  --filled-track-color: ${filledTrackColor || "var(--track-color)"};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  direction: ltr;
  ${variables}
`;

const NumberWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  font-size: 0.813rem;
  box-sizing: border-box;
`;

const track = css`
  box-sizing: border-box;
  border: none;
  border-radius: 8px;
  height: var(--track-height);
  background: linear-gradient(
      to right,
      transparent var(--filled-track-size),
      var(--track-color) var(--filled-track-size)
    ),
    var(--filled-track-color);
  background-position: left center, right center;
  background-repeat: no-repeat;
  position: relative;
  z-index: 1;
`;

const TrackWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`;

const thumb = css`
  box-sizing: border-box;
  border: none;
  width: var(--thumb-size);
  height: var(--thumb-size);
  border-radius: 50%;
  background: #84a4fc;
`;

const Input = styled.input.attrs({ type: "range" })`
  &,
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
  }

  &:focus {
    outline: none;
  }

  margin: 0;
  padding: 0;
  width: 100%;
  height: var(--thumb-size);
  background: transparent;
  font: 1em/1 arial, sans-serif;

  &::-webkit-slider-runnable-track {
    ${track};
  }

  &::-moz-range-track {
    ${track};
  }

  &::-ms-track {
    ${track};
  }

  &::-webkit-slider-thumb {
    margin-top: calc(0.5 * (var(--track-height) - var(--thumb-size)));
    box-shadow: none;
    ${thumb};
  }

  &::-moz-range-thumb {
    ${thumb};
  }

  &::-ms-thumb {
    margin-top: 0;
    ${thumb};
  }

  &::-ms-tooltip {
    display: none;
  }

  &::-moz-focus-outer {
    border: 0;
  }
`;

const TicksMarksWrapper = styled.div`
  visibility: ${({ show }) => (show ? "visible" : "hidden")};
  height: var(--thumb-size);
  display: flex;
  width: calc(100% - var(--thumb-size) + 2px);
  justify-content: space-between;
  position: absolute;
  pointer-events: none;
`;

const TickMark = styled.div`
  width: 0px;
  height: 100%;
  border: 1px solid var(--track-color);
  box-sizing: border-box;
  border-radius: 2px;
  &:first-child {
    border-color: transparent;
  }
  &:last-child {
    border-color: transparent;
  }
`;
